<template>
<div>
  <div class="goback-large">
    <i class="fal fa-arrow-left c-pointer" @click="clickBackButton"></i> [{{deviceName}}] 인터페이스
  </div>
  <el-row type="flex" justify="space-between" class="vertical-center-box title-bar">
    <div class="vertical-center-box">
      <div class="title-h3 mr-r15">인터페이스 목록</div>
      <el-button class="mr-r4" :disabled="checkedRows.length == 0" @click="onDeleteButtonClick">인터페이스 삭제</el-button>
    </div>
    <div>
      <el-button class="mr-r4" size="small" @click="onClickExportExcel"><img src="@/assets/img/icons/excel.png" style="position:relative;top:3px;left:-2px">  엑셀출력</el-button>
      <el-button type="primary" @click="clickSaveButton" :disabled="!cellEditing.isModified">저장</el-button>
      <el-button @click="clickCancelButton" :disabled="!cellEditing.isModified">취소</el-button>
      <w-header-search-button @click="setSearchToolVisible"></w-header-search-button>
    </div>
  </el-row>
  <el-row>
    <el-table-wrapper v-loading="isLoading" :editable="true" :data="netinterfaceTableData" :columns="netinterfaceTableColumns" :column-default="columnDefault" ref="netinterfaceTable" :total=total :pagination="true" :show-search-tool="showSearchTool" :show-custom-header="true"
      @condition-change="loadData" @cell-dblclick="startEdit" @selection-change="changeCheckedRow">
      <template slot-scope="scope" slot="switch-slot">
        <w-switch-editor :editing-prop="cellEditing.property" :data="scope.row" :property="scope.column.property" active-value="1" inactive-value="0" @cell-edit-end="editTwoStatusCell" />
      </template>
      <template slot-scope="scope" slot="input-slot">
        <w-input-editor :editing-prop="cellEditing.property" :data="scope.row" :property="scope.column.property" :required="true" @cell-edit-end="endEdit" />
      </template>
      <template slot-scope="scope" slot="checkbox-slot">
        <w-checkbox-editor :editing-prop="cellEditing.property" :data="scope.row" :property="scope.column.property" true-label="1" false-label="0" @cell-edit-end="editTwoStatusCell" />
      </template>
      <template slot-scope="scope" slot="ifindex-slot">
        <w-number-editor :editing-prop="cellEditing.property" :data="scope.row" :property="scope.column.property" :min="0" @cell-edit-end="endEdit">
          <span slot="content">{{ convertValueForInterfaceSpeed(scope.row.netinterfaceSpeed, 'bps', true, true, true) }}</span>
        </w-number-editor>
      </template>
      <template slot-scope="scope" slot="readonly-checkbox-slot">
        <el-checkbox :value="scope.row.netinterfaceDeleted" true-label="1" false-label="0" />
      </template>
    </el-table-wrapper>
  </el-row>
</div>
</template>

<script>
import { isNil, isEqual, isEmpty, forEach } from 'lodash';
import { columnDefault } from '@/constants/table';
import { getDateToString } from '@/utils/date';
import { EMPTY_TEXT, getValueAfterCheckEmpty } from '@/utils/string';
import { convertValueForInterfaceSpeed } from '@/utils/number';
import msgUtil from '@/utils/message';
import DeviceRegistrationNmsAPI from '@/api/settings/common/device-registration-nms';
import DeviceRegistrationCommonAPI from '@/api/settings/common/device-registration-common';

import CellEditingMixin from '@/mixins/table/cell-editing';
import WInputEditor from '@/components/table/cell-editor/InputEditor';
import WCheckboxEditor from '@/components/table/cell-editor/CheckboxEditor';
import WSwitchEditor from '@/components/table/cell-editor/SwitchEditor';
import WNumberEditor from '@/components/table/cell-editor/NumberEditor';

export default {
  name: 'WNetinterface',
  mixins: [CellEditingMixin],
  components: {
    WInputEditor,
    WCheckboxEditor,
    WSwitchEditor,
    WNumberEditor,
  },
  props: {
    selectedNetdevice: Object,
  },
  watch: {
    selectedNetdevice: {
      handler(object) {
        if (!isNil(object)) {
          this.loadData();
        }
      },
    },
  },
  computed: {
    deviceName() {
      return this.selectedNetdevice ? getValueAfterCheckEmpty(this.selectedNetdevice.objName) : EMPTY_TEXT;
    },
  },
  data() {
    return {
      type: 'rsctype',
      isLoading: false,
      showSearchTool: false,
      netinterfaceTableData: [],
      lastThreeDaysFlag: false,
      rscEventRuleList: [],
      eventAlarmRuleList: [],
      checkedRows: [],
      columnDefault,
      total: 0,
      netinterfaceTableColumns: [
        {
          align: 'center',
          type: 'selection',
        },
        {
          prop: 'objManagementApplyFlag',
          label: '관리여부',
          align: 'center',
          editable: true,
          scopedSlot: 'switch-slot',
          width: 90,
          filters: [
            {
              text: '관리',
              value: '1',
            },
            {
              text: '비관리',
              value: '0',
            },
          ],
          remoteProp: 'o.obj_management_apply_flag',
        },
        {
          prop: 'netinterfaceStatusOper',
          label: 'Oper 상태',
          width: 80,
          filters: [
            {
              text: 'UP',
              value: '1',
            },
            {
              text: 'DOWN',
              value: '2',
            },
          ],
          formatter: this.upDownFormatter,
          remoteProp: 'ni.netinterface_status_oper',
        },
        {
          prop: 'netinterfaceStatusAdmin',
          label: 'Admin 상태',
          width: 80,
          filters: [
            {
              text: 'UP',
              value: '1',
            },
            {
              text: 'DOWN',
              value: '2',
            },
          ],
          formatter: this.upDownFormatter,
          remoteProp: 'ni.netinterface_status_admin',
        },
        {
          prop: 'netinterfaceIfindex',
          label: '인덱스',
          width: 70,
          searchable: true,
          remoteProp: 'ni.netinterface_speed',
        },
        {
          prop: 'objName',
          label: '인터페이스명',
          editable: true,
          scopedSlot: 'input-slot',
          searchable: true,
          remoteProp: 'o.obj_name',
        },
        {
          prop: 'netinterfaceAlias',
          label: '별칭',
          formatter: this.formatterForCheckEmptyValue,
          searchable: true,
          remoteProp: 'ni.netinterface_alias',
        },
        {
          prop: 'ipaddress',
          label: '인터페이스 IP',
          formatter: this.formatterForCheckEmptyValue,
          searchable: true,
          remoteProp: 'ip.ipaddress',
        },
        {
          prop: 'vlanName',
          label: 'VLAN',
          formatter: this.formatterForCheckEmptyValue,
          searchable: true,
          remoteProp: 'vni.netinterface_devicename',
        },
        {
          prop: 'netinterfaceSpeed',
          label: '대역폭',
          editable: true,
          scopedSlot: 'ifindex-slot',
        },
        {
          prop: 'netinterfaceDuplex',
          label: 'Duplex',
          formatter: this.formatterForCheckEmptyValue,
        },
        {
          prop: 'netinterfaceSpeedUpdateFlag',
          label: '대역폭 갱신',
          editable: true,
          scopedSlot: 'checkbox-slot',
          width: 80,
          align: 'center',
        },
        {
          prop: 'netinterfaceDeleted',
          label: '삭제여부',
          width: 60,
          align: 'center',
          scopedSlot: 'readonly-checkbox-slot',
        },
        {
          prop: 'eventExceptFlag',
          label: '이벤트 제외',
          editable: true,
          scopedSlot: 'checkbox-slot',
          width: 80,
          align: 'center',
        },
        {
          prop: 'registDatetime',
          label: '장비 등록일',
          width: 150,
          formatter: this.getDateFormatter,
          searchable: true,
          searchType: 'date',
          remoteProp: 'o.regist_datetime',
        },
      ],
    };
  },
  methods: {
    convertValueForInterfaceSpeed,
    loadData() {
      this.isLoading = true;
      const tableParams = this.$refs.netinterfaceTable.getParameter();
      DeviceRegistrationNmsAPI.getNetinterfaceList({
        objId: this.selectedNetdevice.objId,
        ...tableParams,
      })
        .then((response) => {
          if (response.data.success) {
            this.total = response.data.total;
            this.netinterfaceTableData = this.getEditableTableData(response.data.data, this.netinterfaceTableColumns);
            this.resetCellEditing();
            this.isLoading = false;
          } else {
            this.netinterfaceTableData = [];
            this.total = 0;
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    clickSaveButton() {
      const updatedData = this.getUpdatedDatas(this.netinterfaceTableData, true, ['objId', 'netdeviceId', 'netinterfaceId']);

      const target = '인터페이스';
      const action = msgUtil.action.SAVE;
      DeviceRegistrationNmsAPI.updateNetinterfaceList(updatedData)
        .then((response) => {
          const result = response.data;
          if (result.success) {
            this.loadData();
          }
          this.$message(msgUtil.getResultMsgData(action, target, result.success));
        })
        .catch(() => {
          this.$message(msgUtil.getResultMsgData(action, target, false));
        });
    },
    clickCancelButton() {
      this.loadData();
    },
    setSearchToolVisible() {
      this.showSearchTool = !this.showSearchTool;
    },
    getDateFormatter(row, column, value) {
      if (!isNil(value)) {
        return getDateToString(value);
      }
      return EMPTY_TEXT;
    },
    formatterForCheckEmptyValue(row, column, value) {
      return getValueAfterCheckEmpty(value);
    },
    clickBackButton() {
      this.$emit('click-back-button');
    },
    upDownFormatter(row, column, value) {
      let result = EMPTY_TEXT;
      if (!isNil(value)) {
        if (isEqual(value, 1)) {
          result = 'UP';
        } else if (isEqual(value, 2)) {
          result = 'DOWN';
        }
      }
      return result;
    },
    handlerEndEdit() {
      this.checkExistModifiedData(this.netinterfaceTableData);
    },
    changeCheckedRow(selection) {
      this.checkedRows = selection;
    },
    onDeleteButtonClick() {
      if (!isEmpty(this.checkedRows)) {
        const target = '인터페이스';
        const action = msgUtil.action.DELETE;
        this.$confirm(...msgUtil.getBasicConfirmData(action)).then(() => {
          const objIds = [];
          forEach(this.checkedRows, (item) => {
            objIds.push(item.objId);
          });
          DeviceRegistrationCommonAPI.deleteObjList(objIds)
            .then((response) => {
              if (response.data.success) {
                this.$message(msgUtil.getResultMsgData(action, target, true));
                this.loadData();
              } else {
                this.$message(msgUtil.getResultMsgData(action, target, false));
              }
            })
            .catch(() => {
              this.$message(msgUtil.getResultMsgData(action, target, false));
            });
        });
      }
    },
    onClickExportExcel() {
      const param = {
        objId: this.selectedNetdevice.objId,
      };
      DeviceRegistrationNmsAPI.excelExportNetinterfaceList(param);
    },
  },
};
</script>

<style>
</style>



// WEBPACK FOOTER //
// src/views/settings/basic/DeviceRegistration/Network/components/Netinterface.vue